import cn from 'classnames';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Checkbox, ModalUI, Notifications, showNotification } from 'sonata-react-components';
import { SimplifiedPricingModelEnum } from 'sonata-static-storage/lib/schemas/user/userDataFee.schema';

import { createAssociatedForecastingCampaign, postDataSegment } from '@/resources/audienceTargeting.resources';
import { useGlobalStore } from '@/store/global';
import { getUserRoles } from '@/store/global/userRoles/selectors';
import { AudienceProfileStyled } from './AudienceProfile.styles';
import CampaignEvents from './CampaignEvents/CampaignEvents';
import CustomAudienceProfiles from './CustomAudienceProfiles/CustomAudienceProfiles';
import { getAllowedCustomAudienceProfileTypes } from './customAudienceProfiles.helpers';
import DeviceIds from './DeviceIds/DeviceIds';
import PreviousCampaign from './PreviousCampaign/PreviousCampaign';
import ProfileSettings from './ProfileSettings/ProfileSettings';
import VenueVisits from './VenueVisits/VenueVisits';

import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';

type Events =
  | 'idle'
  | 'profile_settings'
  | 'device_ids'
  | 'previous_campaign_data'
  | 'campaign_events'
  | 'venue_visits';

type Settings = Parameters<typeof postDataSegment>['0'];

type Props = {
  onAddAudienceProfile: () => void;
  user: UserInterface;
};

type State = {
  currentStep: Events;
  option: Events;
  radius: number;
  selectedVenuesIds: string[];
  settings: Partial<Settings>;
};

const getDefaultSettings = (user: UserInterface): Partial<Settings> => {
  const newUserDataSegmentPrice: { [key: string]: number } = {};

  Object.keys(SimplifiedPricingModelEnum).forEach(simplifiedPricingModel => {
    newUserDataSegmentPrice[simplifiedPricingModel] = 0;
  });

  return {
    enabled: true,
    owner: user?._id || user?.id,
    price: newUserDataSegmentPrice,
    status: 'approved',
    user: user?._id || user?.id
  };
};

function AudienceProfile({ onAddAudienceProfile, user }: Props) {
  const userRoles = useGlobalStore(getUserRoles);
  const allowedTypes = getAllowedCustomAudienceProfileTypes(user, userRoles);
  const defaultOption: Events = allowedTypes[0] ?? 'device_ids';
  const [status, setStatus] = useState<'loading' | 'idle'>('idle');
  const [modalKey, setModalKey] = useState(0);
  const [{ currentStep, option, radius, selectedVenuesIds, settings }, setState] = useState<State>({
    currentStep: 'idle',
    option: defaultOption,
    radius: 0,
    selectedVenuesIds: [],
    settings: getDefaultSettings(user)
  });
  const modalRef: any = useRef();
  const { formatMessage } = useIntl();

  const token = {};

  const isDisabled = () => {
    if (
      settings.name &&
      settings.countries &&
      settings.countries.length > 0 &&
      settings.providers &&
      settings.providers.length > 0 &&
      settings.categories &&
      settings.categories.length > 0
    ) {
      switch (currentStep) {
        case 'campaign_events':
          return !(settings.eventTypes && settings.eventTypes.length > 0);
        case 'device_ids':
          return !(settings.s3Paths && settings.s3Paths.length > 0);
        case 'previous_campaign_data':
          return !(settings.campaignIds && settings.campaignIds.length > 0);
        case 'venue_visits':
          return !(selectedVenuesIds && selectedVenuesIds.length > 0);
        default:
          return false;
      }
    }
    return true;
  };

  const newProfileTitle = formatMessage({
    defaultMessage: 'New audience profile',
    id: 'audienceProfile.newProfileTitle'
  });

  const setProfileTitle = formatMessage({
    defaultMessage: 'Set profile',
    id: 'audienceProfile.setProfileTitle'
  });

  const nextButton = formatMessage({
    defaultMessage: 'Profile Settings',
    id: 'audienceProfile.buttons.next'
  });

  const handleResetModal = () => {
    setState(prevState => ({
      ...prevState,
      currentStep: 'idle',
      option: defaultOption,
      settings: getDefaultSettings(user)
    }));
    setModalKey(prevModalKey => prevModalKey + 1);
  };

  const handleStepChange = (step: number) => {
    setState(prevState => ({
      ...prevState,
      currentStep: step === 0 ? 'idle' : option
    }));
  };

  const handleProfileSettingsChange = (newOption: string) => {
    setState(prevState => ({
      ...prevState,
      option: newOption as Events,
      settings: getDefaultSettings(user)
    }));
  };

  const handleSettingsChange = (newSettings: Partial<Settings>) => {
    setState(prevState => ({
      ...prevState,
      settings: {
        ...prevState.settings,
        ...newSettings
      }
    }));
  };

  const handleOwnerChange = (owner: boolean) => {
    setState(prevState => ({
      ...prevState,
      settings: {
        ...prevState.settings,
        owner: owner ? null : user?._id || user?.id
      }
    }));
  };

  const handleAddAudienceProfileClick = async () => {
    try {
      let updatedSettings = structuredClone(settings);
      if (settings) {
        setStatus('loading');
        if (option === 'venue_visits') {
          const associatedCampaign = await createAssociatedForecastingCampaign(
            settings.name,
            radius,
            selectedVenuesIds,
            user._id,
            token
          );

          if (!associatedCampaign || !associatedCampaign._id) {
            throw new Error('The associated campaign could not be created');
          }

          updatedSettings = {
            ...updatedSettings,
            campaignIds: [associatedCampaign._id],
            type: 'campaign-forecasting'
          };
        }

        await postDataSegment(updatedSettings as Settings, token);
        onAddAudienceProfile();
        modalRef?.current?.closeModal?.();
        handleResetModal();
        showNotification(
          'success',
          formatMessage({
            defaultMessage: 'New audience profile successfully added.',
            id: 'audienceProfile.notifications.success'
          })
        );
      }
    } catch {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Something went wrong, please try again.',
          id: 'audienceProfile.notifications.error'
        })
      );
    }
    setStatus('idle');
  };

  const handleOpenClick = () => modalRef?.current?.openModal?.();
  const handleCloseClick = () => {
    handleResetModal();
    modalRef?.current?.closeModal?.();
  };

  const handleRadiusChange = (rd: number) => {
    setState(prevState => ({
      ...prevState,
      radius: rd
    }));
  };

  const handleVenueSettings = (ids: string[]) => {
    setState(prevState => ({
      ...prevState,
      selectedVenuesIds: ids
    }));
  };

  const steps = [
    {
      component: (
        <CustomAudienceProfiles active={option} allowedTypes={allowedTypes} onChange={handleProfileSettingsChange} />
      ),
      description: formatMessage({
        defaultMessage: 'Add custom audience profiles tailored to your campaign needs',
        id: 'audienceProfile.steps.profileSettings.title'
      }),
      title: newProfileTitle
    }
  ];
  if (option === 'device_ids') {
    steps.push({
      component: (
        <div className="audienceProfile__stepTwo">
          <ProfileSettings onChange={handleSettingsChange} />
          <DeviceIds onChange={handleSettingsChange} />
          <div className="audienceProfile__stepTwoCheckbox">
            <Checkbox
              active={!settings.owner}
              id="audienceProfile_owner"
              labelText={formatMessage({
                defaultMessage: 'This audience profile will be visible by all users.',
                id: 'audienceProfile.checkbox.owner'
              })}
              name="owner"
              onFilter={handleOwnerChange}
              type="checkbox"
            />
          </div>
        </div>
      ),
      description:
        currentStep === 'idle'
          ? nextButton
          : formatMessage({
              defaultMessage: 'Profile Settings - Device IDs File',
              id: 'audienceProfile.steps.deviceIds.title'
            }),
      title: setProfileTitle
    });
  }
  if (option === 'previous_campaign_data') {
    steps.push({
      component: (
        <div className="audienceProfile__stepTwo">
          <ProfileSettings onChange={handleSettingsChange} />
          <PreviousCampaign onChange={handleSettingsChange} />
          <div className="audienceProfile__stepTwoCheckbox">
            <Checkbox
              active={!settings.owner}
              id="audienceProfile_owner"
              labelText={formatMessage({
                defaultMessage: 'This audience profile will be visible by all users.',
                id: 'audienceProfile.checkbox.owner'
              })}
              name="owner"
              onFilter={handleOwnerChange}
              type="checkbox"
            />
          </div>
        </div>
      ),
      description:
        currentStep === 'idle'
          ? nextButton
          : formatMessage({
              defaultMessage: 'Profile Settings - Previous Campaign Data',
              id: 'audienceProfile.steps.previousCampaignData.title'
            }),
      title: setProfileTitle
    });
  }
  if (option === 'campaign_events') {
    steps.push({
      component: (
        <div className="audienceProfile__stepTwo">
          <ProfileSettings onChange={handleSettingsChange} />
          <CampaignEvents onChange={handleSettingsChange} />
          <div className="audienceProfile__stepTwoCheckbox">
            <Checkbox
              active={!settings.owner}
              id="audienceProfile_owner"
              labelText={formatMessage({
                defaultMessage: 'This audience profile will be visible by all users.',
                id: 'audienceProfile.checkbox.owner'
              })}
              name="owner"
              onFilter={handleOwnerChange}
              type="checkbox"
            />
          </div>
        </div>
      ),
      description:
        currentStep === 'idle'
          ? nextButton
          : formatMessage({
              defaultMessage: 'Profile Settings - Campaign Events',
              id: 'audienceProfile.steps.campaignEvents.title'
            }),
      title: setProfileTitle
    });
  }
  if (option === 'venue_visits') {
    steps.push({
      component: (
        <div className="audienceProfile__stepTwo">
          <ProfileSettings onChange={handleSettingsChange} />
          <VenueVisits onChange={handleVenueSettings} onRadiusChange={handleRadiusChange} />
          <div className="audienceProfile__stepTwoCheckbox">
            <Checkbox
              active={!settings.owner}
              id="audienceProfile_owner"
              labelText={formatMessage({
                defaultMessage: 'This audience profile will be visible by all users.',
                id: 'audienceProfile.checkbox.owner'
              })}
              name="owner"
              onFilter={handleOwnerChange}
              type="checkbox"
            />
          </div>
        </div>
      ),
      description:
        currentStep === 'idle'
          ? nextButton
          : formatMessage({
              defaultMessage: 'Profile Settings - Venue Visits',
              id: 'audienceProfile.steps.venueVisits.title'
            }),
      title: setProfileTitle
    });
  }

  return (
    <>
      {allowedTypes.length > 0 && (
        <ButtonUI
          icon="fa-solid fa-plus"
          id="audienceProfile_newAudienceButton"
          onClick={handleOpenClick}
          shape="squareRounded"
          size="small"
          text={formatMessage({
            defaultMessage: 'New audience',
            id: 'audienceProfile.buttons.open'
          })}
          type="primary"
        />
      )}
      <AudienceProfileStyled className="audienceProfile">
        <ModalUI
          className={cn('audienceProfile__modal', {
            'audienceProfile__modal--profileTypeStep': currentStep === 'idle'
          })}
          disabledNextButton={false}
          finishButton={{
            disabled: isDisabled(),
            icon: 'fa-solid fa-plus',
            id: 'audienceProfile_finishButton',
            loading: status === 'loading',
            onClick: handleAddAudienceProfileClick,
            text: formatMessage({
              defaultMessage: 'Add Audience Profile',
              id: 'audienceProfile.buttons.finish'
            })
          }}
          key={modalKey}
          leftButton={
            <ButtonUI
              id="audienceProfile_cancelButton"
              onClick={handleCloseClick}
              shape="squareRounded"
              text={formatMessage({
                defaultMessage: 'Cancel',
                id: 'common.cancel'
              })}
              type="secondary"
            />
          }
          onStepChange={handleStepChange}
          ref={modalRef}
          steps={steps}
          title=""
          width="958px"
        />
      </AudienceProfileStyled>
      <Notifications portalId="audienceProfileNotifications" />
    </>
  );
}

export default AudienceProfile;
